import { mount } from './dom';
import {
  CircleScreen,
  DashboardScreen,
  EditProfileScreen,
  FriendDetailScreen,
  GroupsScreen,
  InviteScreen,
  RecoverScreen,
  SettingsScreen,
  ShareIdScreen,
  WelcomeScreen,
} from './screens';
import { currentScreen, navigate, startRouter, type Screen } from './router';
import { currentIdentity, loadIdentity } from '../core/identity';
import { ensurePersistentStorage } from '../core/persistence';
import { startSyncManager } from '../core/syncManager';

function view(screen: Screen): HTMLElement {
  switch (screen.name) {
    case 'welcome':
      return WelcomeScreen();
    case 'recover':
      return RecoverScreen();
    case 'dashboard':
      return DashboardScreen();
    case 'editProfile':
      return EditProfileScreen();
    case 'circle':
      return CircleScreen();
    case 'friendDetail':
      return FriendDetailScreen(screen.circleId);
    case 'invite':
      return InviteScreen(screen.circleId);
    case 'shareId':
      return ShareIdScreen();
    case 'settings':
      return SettingsScreen();
    case 'groups':
      return GroupsScreen();
  }
}

export async function startApp(root: HTMLElement): Promise<void> {
  // Ask the browser to keep our data out of automatic eviction (best-effort;
  // the recovery phrase remains the ultimate backup).
  await ensurePersistentStorage();
  await loadIdentity();

  // Play the enter transition only when the section actually changes, so moving
  // between screens feels like a soft settle while in-place re-renders (a
  // background sync, a list filter) don't flicker.
  let prevName = '';
  const render = (): void => {
    const screen = currentScreen(!!currentIdentity());
    const el = view(screen);
    if (screen.name !== prevName) {
      (el.querySelector('.content-inner') ?? el.querySelector('.centered-inner'))?.classList.add('screen-enter');
    }
    prevName = screen.name;
    mount(root, el);
  };

  // The URL is the source of truth for which screen shows, so a refresh or a
  // shared deep link lands on the right view and back/forward stay consistent.
  startRouter(render);

  // Re-render only the screens that display friends' data, so a background sync
  // never clobbers a form the user is filling in.
  const refreshIfViewingFriends = (): void => {
    const s = currentScreen(!!currentIdentity());
    if (s.name === 'circle' || s.name === 'friendDetail' || s.name === 'dashboard') render();
  };

  // Start the background sync the moment an identity exists — at load for a
  // returning user, or right after onboarding for a brand-new one (the identity
  // doesn't exist yet when startApp first runs). Guarded so it only starts once.
  let syncStarted = false;
  const ensureSync = (): void => {
    if (syncStarted) return;
    const me = currentIdentity();
    if (!me) return;
    syncStarted = true;
    // Pulling my own state down is part of every sync tick now (it's what keeps
    // this device level with the user's other ones), so the sync manager owns
    // it — no separate call here.
    // Keep friends' details fresh: sync on foreground/focus/online and on a
    // heartbeat, with a live socket on top. A misconfigured/unreachable relay
    // degrades quietly (reflected in the sync indicator), never as a crash.
    startSyncManager(me, refreshIfViewingFriends);
  };

  // Re-check after every navigation so onboarding → dashboard kicks it off.
  window.addEventListener('hashchange', ensureSync);
  ensureSync();
}

// Re-exported so other modules can navigate without importing the router path.
export { navigate };
